import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import DotsMenuIcon from '../../../icons/svg/dots_menu.svg?react';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SxProps, Theme } from '@mui/material/styles';
import { TooltipComponent } from '../../atomic/feedback';

export interface ActionMenuItem {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  color?: string;
  hoverColor?: string;
  hoverBgColor?: string;
  disabled?: boolean;
  hasDivider?: boolean;
  tooltipText?: string;
  disabledTooltip?: boolean;
}

interface ActionsMenuCellProps {
  items: ActionMenuItem[];
  className?: string;
  menuItemStyle?: SxProps<Theme>;
  disabled?: boolean;
}

export function ActionsMenuCell({
  items,
  className,
  menuItemStyle,
  disabled = false
}: Readonly<ActionsMenuCellProps>) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { t } = useTranslation();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleItemClick = (onClick: () => void) => {
    onClick();
    handleClose();
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        size="small"
        className={className}
        aria-label={t('common:MORE_OPTIONS')}
        disabled={disabled}
      >
        <DotsMenuIcon
          className={`${disabled ? 'text-grey-300' : 'text-grey-600'} hover:text-primary-main`}
        />{' '}
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        BackdropProps={{
          invisible: true,
          style: { backgroundColor: 'transparent', opacity: '0' }
        }}
        PaperProps={{ className: '!shadow-500' }}
        sx={{
          '& .MuiPaper-root': {
            boxShadow: 'inherit',
            '& .MuiList-root': {
              padding: 0
            }
          }
        }}
      >
        {items.map((item) => {
          const menuItem = (
            <MenuItem
              onClick={() => handleItemClick(item.onClick)}
              disabled={item.disabled}
              className="pl-4 pr-6 py-3"
              sx={{
                color: item.color,
                borderTop: item.hasDivider ? '1px solid' : '',
                borderColor: item.hasDivider ? 'grey.100' : '',
                '& .MuiListItemIcon-root': {
                  color: item.color
                },
                '& .MuiListItemText-primary': {
                  color: item.color
                },
                '&:hover': {
                  color: item.hoverColor,
                  bgcolor: item.hoverBgColor
                },
                ...menuItemStyle
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText>{item.label}</ListItemText>
            </MenuItem>
          );

          return (
            <Fragment key={item.label}>
              {item.disabled && item.tooltipText ? (
                <TooltipComponent arrow title={item.tooltipText}>
                  <span>{menuItem}</span>
                </TooltipComponent>
              ) : (
                menuItem
              )}
            </Fragment>
          );
        })}
      </Menu>
    </>
  );
}
